import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { queryClient, trpc, trpcErrorCode, trpcErrorDetails } from "../../app/trpc.js";
import { Button, Card, Chip, FormRow, Input, Label, Notice, PageTitle, SectionTitle, Spinner, Toggle } from "../../ui/index.js";
import { addDays, dayKey, dayStart, fmtDateTime, useCabinet } from "../cabinet/index.js";
import { HoursGrid, HoursSaveState, LeaveGuard, useHoursAutosave } from "../onboarding/index.js";
import { invalidateBookings } from "./invalidate.js";
import { TimeOffSheet } from "./TimeOffSheet.js";

/**
 * Мастер: имя, услуги, выключение (владелец), график по дням недели (несколько интервалов в день),
 * отгулы и больничные. Мастер открывает здесь свой график. График тот же, что в мастере настройки (`HoursGrid`),
 * и сохраняется сам, без кнопки (`useHoursAutosave`).
 * На широком экране две колонки: слева карточка мастера и отгулы, справа график.
 */
export function MasterPage() {
  const { masterId = "" } = useParams();
  // Другой мастер: новая страница целиком, иначе автосохранение графика прежнего мастера ушло бы новому.
  return <MasterPageInner key={masterId} masterId={masterId} />;
}

function MasterPageInner({ masterId }: { masterId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const cab = useCabinet();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId, includeInactive: true }, { enabled: cab.isOwner }));
  const services = useQuery(trpc.calendar.services.list.queryOptions({ salonId: cab.salonId }, { enabled: cab.isOwner }));
  const hours = useQuery(trpc.calendar.workingHours.get.queryOptions({ masterId }));
  const range = useMemo(() => {
    const today = dayKey(new Date(), cab.timezone);
    return { from: dayStart(today, cab.timezone), to: dayStart(addDays(today, 90), cab.timezone) };
  }, [cab.timezone]);
  const offs = useQuery(trpc.calendar.timeOff.list.queryOptions({ masterId, ...range }));
  const master = masters.data?.find((m) => m.id === masterId);

  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [offOpen, setOffOpen] = useState(false);

  useEffect(() => {
    if (master) {
      setName(master.name);
      setActive(master.active);
      setServiceIds(master.serviceIds);
    }
  }, [master]);
  const autosave = useHoursAutosave(masterId, hours.data);

  const saveMaster = useMutation(
    trpc.calendar.masters.save.mutationOptions({ onSuccess: () => queryClient.invalidateQueries(trpc.calendar.masters.pathFilter()) }),
  );
  const remove = useMutation(
    trpc.calendar.masters.remove.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.calendar.masters.pathFilter());
        // Пользователь кабинета мог быть привязан к удалённому мастеру, а контекст кэшируется на 5 минут.
        void queryClient.invalidateQueries(trpc.cabinet.context.pathFilter());
        navigate(`${cab.base}/settings/masters`);
      },
      onError: () => {
        setDeleting(false);
        autosave.resume();
      },
    }),
  );
  // Удаление мастера: несохранённый график ему уже не нужен, ни запрос при уходе, ни вопрос «Остаться или уйти».
  const [deleting, setDeleting] = useState(false);
  const onRemove = () => {
    setDeleting(true);
    autosave.pause();
    remove.mutate({ salonId: cab.salonId, masterId });
  };
  const delOff = useMutation(
    trpc.calendar.timeOff.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.calendar.timeOff.pathFilter());
        invalidateBookings();
      },
    }),
  );

  if (hours.isError) return <Notice kind="error">{t("masters.notYours")}</Notice>;
  if (cab.loading || hours.isPending || !autosave.grid) return <Spinner />;

  return (
    <div>
      <PageTitle>{master?.name ?? t("masters.mySchedule")}</PageTitle>

      {/* Вторая строка сетки забирает высоту длинного графика, первая по высоте карточки: отгулы сразу под ней. */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] lg:grid-rows-[auto_1fr] lg:items-start lg:gap-6">
        <div className="lg:col-start-1">
          {cab.isOwner && master && (
            <Card className="space-y-4">
              <FormRow label={t("masters.name")} htmlFor="m-name">
                <Input id="m-name" value={name} onChange={(e) => setName(e.target.value)} />
              </FormRow>
              <div>
                <Label>{t("masters.services")}</Label>
                <div className="flex flex-wrap gap-2">
                  {services.data?.map((s) => {
                    const on = serviceIds.includes(s.id);
                    return (
                      <Chip
                        key={s.id}
                        active={on}
                        onClick={() => setServiceIds((cur) => (cur.includes(s.id) ? cur.filter((x) => x !== s.id) : [...cur, s.id]))}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span className={on ? "inline-block w-4" : "inline-block w-4 text-transparent"} aria-hidden="true">
                            ✓
                          </span>
                          {s.name}
                        </span>
                      </Chip>
                    );
                  })}
                </div>
                <p className="mt-1 text-sm text-ink-3">{t("masters.servicesHint")}</p>
              </div>
              <Toggle checked={active} onChange={setActive} label={t("masters.active")} hint={t("masters.activeHint")} />
              {saveMaster.isError && <Notice kind="error">{t("common.error")}</Notice>}
              {saveMaster.isSuccess && <Notice kind="success">{t("settings.saved")}</Notice>}
              <div className="flex flex-col gap-2 lg:flex-row lg:items-start">
                <Button
                  className="w-full lg:w-auto"
                  disabled={!name.trim() || saveMaster.isPending}
                  onClick={() => saveMaster.mutate({ id: masterId, salonId: cab.salonId, name: name.trim(), active, serviceIds })}
                >
                  {t("settings.save")}
                </Button>
                {!confirmDelete && (
                  <Button variant="secondary" className="w-full lg:w-auto" onClick={() => setConfirmDelete(true)}>
                    {t("common.remove")}
                  </Button>
                )}
              </div>
              {confirmDelete && (
                <div className="space-y-2">
                  <p className="text-sm text-ink-2">{t("masters.deleteAsk", { name: master.name })}</p>
                  {remove.isError && (
                    <Notice kind="error">
                      {trpcErrorCode(remove.error) !== "conflict"
                        ? t("common.error")
                        : trpcErrorDetails(remove.error)?.reason === "linked"
                          ? t("masters.deleteLinked")
                          : t("masters.deleteBlocked")}
                    </Notice>
                  )}
                  <div className="flex gap-2">
                    <Button variant="secondary" className="flex-1" onClick={() => setConfirmDelete(false)}>
                      {t("common.cancel")}
                    </Button>
                    <Button className="flex-1" disabled={remove.isPending} onClick={onRemove}>
                      {t("common.remove")}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>

        <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <SectionTitle className={cab.isOwner && master ? "lg:mt-0" : ""}>{t("masters.hours")}</SectionTitle>
          <Card>
            <HoursGrid grid={autosave.grid} onChange={autosave.setGrid} />
          </Card>
          <div className="mt-2">
            <HoursSaveState status={autosave.status} onRetry={autosave.retry} />
          </div>
        </div>

        <div className="lg:col-start-1">
          <SectionTitle>{t("masters.timeOff")}</SectionTitle>
          {offs.data?.length === 0 && <Notice>{t("masters.noTimeOff")}</Notice>}
          <div className="space-y-3">
            {offs.data?.map((o) => (
              <Card key={o.id} className="flex items-center justify-between gap-2">
                <div className="text-sm">
                  <div className="font-medium">{t(`cal.offKind.${o.kind}`)}</div>
                  <div className="text-ink-3">
                    {fmtDateTime(o.period.start, cab.timezone, i18n.language)} – {fmtDateTime(o.period.end, cab.timezone, i18n.language)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" disabled={delOff.isPending} onClick={() => delOff.mutate({ id: o.id })}>
                  {t("common.remove")}
                </Button>
              </Card>
            ))}
          </div>
          <Button variant="secondary" className="mt-4 w-full lg:w-auto" onClick={() => setOffOpen(true)}>
            {t("cal.timeOff")}
          </Button>
        </div>
      </div>
      <LeaveGuard blockRoutes={autosave.unsaved && !deleting} blockUnload={autosave.notOnServer && !deleting} />
      <TimeOffSheet open={offOpen} onClose={() => setOffOpen(false)} day={dayKey(new Date(), cab.timezone)} defaultMasterId={masterId} />
    </div>
  );
}
